import api from '@/shared/utils/api';
import type {
  AgentAvailabilityPatch,
  AgentBindingView,
  AgentCalendarStatus,
  AgentCatalogResponse,
  AgentCoverage,
  AgentCoverageListResponse,
  AgentProfile,
  AgentProfilePatch,
  AgentRehearsal,
  AgentReturnResponse,
  AgentSubscription,
  AgentSubscriptionCancel,
  AgentTurnsResponse,
  AgentesUser,
  KnowledgeItem,
  KnowledgeKind,
  KnowledgeListResponse,
  KnowledgeUploadResponse,
  RehearseRequestBody,
  RehearsalAuthorizedNumber,
  RehearsalLiveSession,
  RehearsalTemplatesResponse,
  AgentConversationsResponse,
  AgentEvolutionStatusResponse,
  AgentEvolutionSuggestion,
  AgentEvolutionPacingConfig,
  RunAnalysisResponse,
  UpdatePacingRequest,
  CopilotChatMessage,
  CopilotChatResponse,
  CopilotChatHistoryResponse,
  CopilotChatClearResponse,
  CopilotProposal,
  ApplyProposalResponse,
} from '../types';

/**
 * Equipe de Agentes — cliente tipado do proxy `/api/v1/agents/**` do BFF.
 *
 * Tudo passa pelo `apiClient` compartilhado (baseURL `/api/v1`, o BFF). O
 * frontend nunca fala com a sales-api nem com o agent-ai diretamente (regra
 * de arquitetura do CLAUDE.md).
 *
 * ── Duas convenções de caso, e por quê ────────────────────────────────
 * `/agents/**` é passthrough CRU no BFF: pede e devolve snake_case. Já
 * `/users` tem mapper (`transformUser`) e devolve camelCase. Este arquivo é o
 * único lugar do módulo onde essa costura aparece — as telas consomem os
 * tipos e não precisam saber.
 *
 * ── Uma divergência MEDIDA entre spec e código ────────────────────────
 * A spec (07 §4.1) escreve a listagem como
 * `GET /api/v1/users?seat_type=&active_only=true`. O BFF, porém, só declara
 * `activeOnly` no `listUsersValidation` e só lê `req.query.activeOnly`
 * (`src/routes/auth/users.routes.ts:69,110`) — mandar `active_only` passaria
 * o filtro como `undefined` e traria TAMBÉM os usuários desativados, que é o
 * oposto do pedido. O `seat_type` é lido nas duas grafias
 * (`req.query.seat_type ?? req.query.seatType`, `:111`). Logo: `seat_type` +
 * `activeOnly`. O código venceu a spec.
 */

const BASE = '/agents';
const KNOWLEDGE = `${BASE}/knowledge`;
const MESSAGING = `${BASE}/messaging`;

export const agentesApi = {
  // ── Catálogo + assinatura ──────────────────────────────────────────
  async getCatalog(planSlug?: string): Promise<AgentCatalogResponse> {
    const { data } = await api.get<AgentCatalogResponse>(`${BASE}/catalog`, {
      params: planSlug ? { plan: planSlug } : undefined,
    });
    return data;
  },

  async getSubscription(): Promise<AgentSubscription> {
    const { data } = await api.get<AgentSubscription>(`${MESSAGING}/subscription`);
    return data;
  },

  /**
   * Contrata para N usuários. `quantity = user_ids.length`, coberturas nascem
   * em **N1** (§4.4: N1 é obrigatório para todo agente novo).
   *
   * Recusas que a tela precisa distinguir e que chegam COM o código, porque o
   * BFF nunca as achata: **402** `PLAN_TIER_REQUIRED` (plano) · **403**
   * `FEATURE_NOT_ENABLED` (entitlement) · **409** `COVERAGE_EXCEEDS_QUANTITY`.
   */
  async subscribe(
    userIds: number[],
    idempotencyKey: string,
    agentId?: string,
  ): Promise<AgentSubscription> {
    // E-9a: `agent_id` presente = contratar PARA uma instância (outbound
    // assina o addon do cargo e carimba as coberturas nela). Ausente = o
    // fluxo legado, byte a byte — por isso o campo só entra quando existe.
    const { data } = await api.post<AgentSubscription>(`${MESSAGING}/subscription`, {
      user_ids: userIds,
      idempotency_key: idempotencyKey,
      ...(agentId ? { agent_id: agentId } : {}),
    });
    return data;
  },

  async changeQuantity(quantity: number, idempotencyKey: string): Promise<AgentSubscription> {
    const { data } = await api.patch<AgentSubscription>(`${MESSAGING}/subscription`, {
      quantity,
      idempotency_key: idempotencyKey,
    });
    return data;
  },

  /**
   * Desliga o agente do tenant inteiro (US-AG-01, o passo "Afastar" — WP-16):
   * todas as coberturas → `off` + add-on cancelado no billing, num fluxo só.
   *
   * Recusa que a tela precisa distinguir: `SUBSCRIPTION_CANCEL_UNAVAILABLE`
   * (o billing não confirmou; **nada** foi desligado; repita o clique).
   * Cancelar o já-cancelado é 200 no-op (`already_cancelled: true`).
   */
  async cancelSubscription(
    idempotencyKey: string,
    reason?: string,
  ): Promise<AgentSubscriptionCancel> {
    const { data } = await api.post<AgentSubscriptionCancel>(`${MESSAGING}/subscription/cancel`, {
      idempotency_key: idempotencyKey,
      ...(reason ? { reason } : {}),
    });
    return data;
  },

  // ── Coberturas ─────────────────────────────────────────────────────
  async listCoverages(windowDays = 30): Promise<AgentCoverageListResponse> {
    const { data } = await api.get<AgentCoverageListResponse>(`${MESSAGING}/coverages`, {
      params: { window_days: windowDays },
    });
    return data;
  },

  async createCoverage(userId: number, authorityLevel: 'n1' | 'n2' = 'n1'): Promise<AgentCoverage> {
    const { data } = await api.post<AgentCoverage>(`${MESSAGING}/coverages`, {
      user_id: userId,
      authority_level: authorityLevel,
    });
    return data;
  },

  /**
   * Promoção N1→N2 é clique do admin com os números na tela (RN-20, onda 1);
   * o rebaixamento é imediato e não exige justificativa. Tirar a cobertura é
   * `status:'off'` — **nunca** um DELETE.
   */
  async patchCoverage(
    userId: number,
    patch: { status?: string; authority_level?: string },
  ): Promise<AgentCoverage> {
    const { data } = await api.patch<AgentCoverage>(`${MESSAGING}/coverages/${userId}`, patch);
    return data;
  },

  /** Kill switch nível 1 — o vendedor se pausa sem pedir autorização (§4.10). */
  async setSelfPause(paused: boolean): Promise<AgentCoverage> {
    const { data } = await api.patch<AgentCoverage>(`${MESSAGING}/coverages/me/pause`, { paused });
    return data;
  },

  // ── Perfil (os seis blocos) ────────────────────────────────────────
  /**
   * `agentId` (WP-V2-8) escopa o perfil por instância — Inbound e Outbound
   * deixaram de compartilhar uma vigente só (2026-08-29: editar um
   * reescrevia o outro). Ausente = a vigente do tenant, byte a byte
   * (tenant com 0-1 instância).
   */
  async getProfile(agentId?: string): Promise<AgentProfile> {
    const { data } = await api.get<AgentProfile>(`${MESSAGING}/profile`, {
      ...(agentId ? { params: { agent_id: agentId } } : {}),
    });
    return data;
  },

  /** Salvar CRIA a versão N+1 — o recibo de ontem segue legível com a config
   * da época. `agentId` (WP-V2-8): igual à leitura — sem ele, um tenant com
   * mais de uma instância recebe 409 `AGENT_SCOPE_REQUIRED` em vez de
   * adivinhar qual agente está sendo editado. */
  async saveProfile(patch: AgentProfilePatch, agentId?: string): Promise<AgentProfile> {
    const { data } = await api.put<AgentProfile>(`${MESSAGING}/profile`, patch, {
      ...(agentId ? { params: { agent_id: agentId } } : {}),
    });
    return data;
  },

  /** Sincroniza o contexto corporativo e comercial com o seller_context do agente */
  async syncCompanyContext(agentId?: string): Promise<AgentProfile> {
    const { data } = await api.post<AgentProfile>(`${MESSAGING}/sync-company-context`, {}, {
      ...(agentId ? { params: { agent_id: agentId } } : {}),
    });
    return data;
  },

  // ── Agenda do vendedor ─────────────────────────────────────────────
  async getMyAvailability(): Promise<AgentCoverage> {
    const { data } = await api.get<AgentCoverage>(`${MESSAGING}/availability/me`);
    return data;
  },

  async saveMyAvailability(patch: AgentAvailabilityPatch): Promise<AgentCoverage> {
    const { data } = await api.put<AgentCoverage>(`${MESSAGING}/availability/me`, patch);
    return data;
  },

  /** Agregador que promete 200 sempre — a tela lê `degraded`, não um erro. */
  async getMyCalendarStatus(): Promise<AgentCalendarStatus> {
    const { data } = await api.get<AgentCalendarStatus>(`${MESSAGING}/calendar-status/me`);
    return data;
  },

  // ── Vínculo agente ↔ número (o tenant_admin escolhe na tela) ───────
  async getBinding(): Promise<AgentBindingView> {
    const { data } = await api.get<AgentBindingView>(`${MESSAGING}/binding`);
    return data;
  },

  async setBinding(phoneNumberId: string): Promise<AgentBindingView> {
    const { data } = await api.put<AgentBindingView>(`${MESSAGING}/binding`, {
      phone_number_id: phoneNumberId,
    });
    return data;
  },

  // ── Ensaio ─────────────────────────────────────────────────────────
  /**
   * Nada persiste, nada cobra, nada é criado (§4.9). `history`/`slot_attempts`
   * (opcionais — 2026-08-31, chat estilo WhatsApp na tela) deixam o ensaio
   * virar uma conversa de verdade: o front carrega o histórico simulado a
   * cada chamada, o serviço continua sem memória própria.
   */
  async rehearse(body: RehearseRequestBody): Promise<AgentRehearsal> {
    const { data } = await api.post<AgentRehearsal>(`${MESSAGING}/rehearse`, body);
    return data;
  },

  // ── Base de conhecimento (texto colado OU upload de PDF) ────────────
  async listKnowledge(includeInactive = false): Promise<KnowledgeListResponse> {
    const { data } = await api.get<KnowledgeListResponse>(`${KNOWLEDGE}/items`, {
      params: { include_inactive: includeInactive },
    });
    return data;
  },

  async createKnowledge(item: {
    kind: KnowledgeKind;
    title: string;
    body: string;
    citation_label?: string;
    /** all | inbound | outbound (WP-V2-2) — omitido = `all` no backend. */
    audience?: string;
  }): Promise<KnowledgeItem> {
    // `created_by_user_id` é injetado pelo BFF a partir do JWT validado e
    // NUNCA pelo corpo — mandá-lo daqui deixaria um admin arquivar
    // conhecimento em nome de outro.
    const { data } = await api.post<KnowledgeItem>(`${KNOWLEDGE}/items`, item);
    return data;
  },

  /**
   * Upload de PDF (WP-21) — pedido do dono testando o produto ao vivo,
   * reverte o corte de escopo da wave 1 (o comentário acima dizia "arquivo é
   * onda 2"). O backend extrai o texto, particiona em trechos e devolve UM
   * item por trecho — por isso a resposta é `KnowledgeUploadResponse`
   * (plural), diferente de `createKnowledge` (singular). `kind` é opcional
   * (default `material` no backend).
   *
   * `onProgress` é opcional e alimenta a barra de progresso do upload —
   * `axios` reporta `progressEvent.total` só quando o servidor/proxy expõe
   * `Content-Length`, então a tela trata `undefined` como "sem percentual
   * conhecido ainda", nunca como 0%.
   */
  async uploadKnowledgeFile(
    file: File,
    kind?: KnowledgeKind,
    onProgress?: (percent: number | undefined) => void,
    audience?: string,
  ): Promise<KnowledgeUploadResponse> {
    const formData = new FormData();
    formData.append('file', file);
    if (kind) formData.append('kind', kind);
    // WP-V2-2/E-9d — ausente = 'all' no backend (comportamento de hoje).
    if (audience) formData.append('audience', audience);
    const { data } = await api.post<KnowledgeUploadResponse>(
      `${KNOWLEDGE}/items/upload`,
      formData,
      {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: onProgress
          ? (evt) => {
              const percent =
                typeof evt.total === 'number' && evt.total > 0
                  ? Math.round((evt.loaded / evt.total) * 100)
                  : undefined;
              onProgress(percent);
            }
          : undefined,
      },
    );
    return data;
  },

  async deleteKnowledge(itemId: string): Promise<{ deleted: boolean; id: string }> {
    const { data } = await api.delete<{ deleted: boolean; id: string }>(
      `${KNOWLEDGE}/items/${encodeURIComponent(itemId)}`,
    );
    return data;
  },

  // ── Handoff da conversa + recibo (WP-17, o overlay da Caixa de Entrada) ──

  /** GET /agents/messaging/turns?deal_id= — o recibo é chaveado por DEAL. */
  async listTurns(dealId: string, limit = 50): Promise<AgentTurnsResponse> {
    const { data } = await api.get<AgentTurnsResponse>(`${MESSAGING}/turns`, {
      params: { deal_id: dealId, limit },
    });
    return data;
  },

  /**
   * Devolver a conversa ao agente (RN-11) — SÓ por clique explícito, nunca
   * automático.
   *
   * `channel` (2026-09-01): a trava do agente é por (deal, CANAL) — um deal
   * pode ter conversa de WhatsApp E de Instagram. Sem mandá-lo, o backend
   * assumia `whatsapp`: devolver uma conversa de Instagram respondia 200,
   * criava uma linha de WhatsApp que ninguém pediu e deixava a de Instagram
   * encerrada. A tela dizia "devolvida" e o agente seguia mudo.
   *
   * Omitido ⇒ o backend mantém o default `whatsapp`, byte a byte.
   * `phone` (opcional): quando informado, afeta estritamente a thread daquele contato.
   */
  async returnConversationToAgent(dealId: string, channel?: string, phone?: string): Promise<AgentReturnResponse> {
    const { data } = await api.post<AgentReturnResponse>(
      `${MESSAGING}/conversations/${encodeURIComponent(dealId)}/return`,
      {
        ...(channel ? { channel } : {}),
        ...(phone ? { phone } : {}),
      },
    );
    return data;
  },

  /**
   * Assumir a conversa (US-AG-11, botão "Assumir esta conversa" — 10-UX
   * §5.3). MESMO gate de `returnConversationToAgent` (dono do deal ou
   * gestor).
   * `phone` (opcional): quando informado, assume estritamente a thread daquele contato.
   */
  async assumeConversation(dealId: string, channel?: string, phone?: string): Promise<AgentReturnResponse> {
    const { data } = await api.post<AgentReturnResponse>(
      `${MESSAGING}/conversations/${encodeURIComponent(dealId)}/assume`,
      // Mesmo motivo do `/return` — e aqui errar é pior: o vendedor clica
      // "Assumir" para o agente PARAR, e travar o canal errado deixaria o
      // agente do Instagram conduzindo enquanto a tela diz que ele parou.
      {
        ...(channel ? { channel } : {}),
        ...(phone ? { phone } : {}),
      },
    );
    return data;
  },

  // ── Usuários do tenant (camelCase — rota com mapper) ───────────────
  /**
   * A lista da tela de contratação, com a credencial do PRÓPRIO admin.
   *
   * ⚠️ Nunca trocar por `GET {tenant-mgmt}/api/v1/tenants/{id}/users`: aquela
   * rota devolve usuários MOCK hardcoded (07 §4.1).
   */
  async listUsers(seatType?: 'sales' | 'executive'): Promise<AgentesUser[]> {
    const { data } = await api.get<{ users?: unknown[] } | unknown[]>('/users', {
      params: { activeOnly: true, ...(seatType ? { seat_type: seatType } : {}) },
    });
    const rows =
      (Array.isArray(data) ? data : ((data as { users?: unknown[] })?.users ?? [])) ?? [];
    return rows.map((raw) => {
      const u = raw as Record<string, unknown>;
      const first = (u.firstName as string) ?? '';
      const last = (u.lastName as string) ?? '';
      const composed = `${first} ${last}`.trim();
      return {
        id: String(u.id ?? ''),
        email: String(u.email ?? ''),
        name: (u.name as string) || composed || String(u.email ?? ''),
        role: (u.role as string) ?? (Array.isArray(u.roles) ? String(u.roles[0] ?? '') : ''),
        seatType: (u.seatType as string | null) ?? (u.seat_type as string | null) ?? null,
        isActive: (u.isActive as boolean) ?? (u.active as boolean) ?? true,
      };
    });
  },
};

export default agentesApi;

// ── Equipe V2 (WP-V2-6) — instâncias, cards e dossiê ────────────────────
// Anexado ao objeto para não tocar as chamadas existentes.

import type {
  AgentBindingOptions,
  AgentDrainingStatus,
  AgentEmailMailbox,
  AgentEmailDelegationInfo,
  AgentEmailMailboxConnectBody,
  AgentEmailMailboxConnectResponse,
  AgentEmailMailboxDisconnectResponse,
  AgentEmailMailboxProbeBody,
  AgentEmailMailboxProbeResponse,
  AgentEmailMailboxTestResponse,
  AgentEmailMailboxTipo,
  AgentInstance,
  AgentInstanceDeleted,
  AgentInstanceEventsResponse,
  AgentInstanceMetrics,
  AgentMetricDetailKind,
  AgentMetricDetailResponse,
  AgentPieceRehearsal,
  AgentTeamResponse,
} from '../types';

export const agentesTeamApi = {
  /** O agregador da ENTRADA de /agentes — cards com saúde/custo/resumo. */
  async getTeam(): Promise<AgentTeamResponse> {
    const { data } = await api.get<AgentTeamResponse>(`${BASE}/team`);
    return data;
  },

  async listInstances(): Promise<{ agents: AgentInstance[] }> {
    const { data } = await api.get<{ agents: AgentInstance[] }>(BASE);
    return data;
  },

  /** R-1: 409 EXISTING_AGENT_UNBOUND enquanto houver instância sem canal. */
  /**
   * `channel` é OBRIGATÓRIO em `inbound` desde 2026-09-01 (um agente Inbound
   * atende um canal só) e IGNORADO em `outbound`, que é channel-agnóstico por
   * construção — a cadência é dirigida pelo canal do PASSO, não pelo agente.
   *
   * Omiti-lo em `inbound` devolve 422 `AGENT_CHANNEL_REQUIRED`; é o estado em
   * que esta tela ficou entre o deploy do backend e o desta versão.
   */
  async createInstance(
    role: 'inbound' | 'outbound',
    displayName: string,
    channel?: string,
  ): Promise<AgentInstance> {
    const { data } = await api.post<AgentInstance>(BASE, {
      role,
      display_name: displayName,
      // Ausente (não `null`) quando não há canal: o backend distingue "campo
      // não veio" de "veio vazio", e mandar `null` em outbound seria afirmar
      // um canal nulo em vez de não falar do assunto.
      ...(channel ? { channel } : {}),
    });
    return data;
  },

  /** Kill switch POR instância (RN-79) — pausar um nunca afeta o outro. */
  async setInstancePaused(agentId: string, paused: boolean): Promise<AgentInstance> {
    const { data } = await api.patch<AgentInstance>(`${BASE}/${encodeURIComponent(agentId)}`, {
      paused,
    });
    return data;
  },

  /**
   * Excluir uma instância INERTE — pausada e sem rastro em nenhuma tabela
   * (vínculo, cobertura, perfil, conversa, recibo, sessão). É o fantasma da
   * contratação que falhou no meio (medido no 4670: `paused`, `binding:
   * null`, zero caixas). Qualquer rastro volta 409 `AGENT_NOT_INERT` com
   * `details.bloqueios` nomeando o que impede — a tela mostra a frase do
   * backend, nunca uma inventada aqui. Um agente que já operou se PAUSA.
   */
  async deleteInstance(agentId: string): Promise<AgentInstanceDeleted> {
    const { data } = await api.delete<AgentInstanceDeleted>(
      `${BASE}/${encodeURIComponent(agentId)}`,
      { ...({ __skipErrorNotification: true } as object) },
    );
    return data;
  },

  /**
   * Vincular número à instância. As recusas chegam COM o código (o BFF nunca
   * as achata): BINDING_NUMBER_NOT_IN_ACCOUNT · BINDING_PURPOSE_PLATFORM ·
   * BINDING_PURPOSE_UNDECLARED · BINDING_NUMBER_TAKEN ·
   * BINDING_AGENT_ALREADY_BOUND (troca exige confirm_swap — a drenagem é
   * explicada ANTES do clique) · BINDING_EXCEEDS_QUANTITY (aumente a
   * assinatura do Agente Inbound) · PRICE_NOT_CONFIRMED (âncora do dono).
   */
  async bindInstanceNumber(
    agentId: string,
    phoneNumberId: string,
    confirmSwap = false,
  ): Promise<AgentInstance> {
    const { data } = await api.post<AgentInstance>(
      `${BASE}/${encodeURIComponent(agentId)}/binding`,
      { phone_number_id: phoneNumberId, confirm_swap: confirmSwap },
    );
    return data;
  },

  /**
   * Os endpoints do CANAL da instância (migrações 150/152 — um agente por
   * número/conta/caixa): `GET /agents/{id}/binding/options`. A MESMA
   * disciplina do número para Instagram e e-mail: `vinculavel`/
   * `motivo_bloqueio` resolvidos no backend, ocupação por outro agente DITA
   * em `held_by`, fonte fora do ar degrada (`endpoints_unavailable`) — nunca
   * 500. LinkedIn/Facebook não têm endpoint vinculável e respondem 422
   * `BINDING_CHANNEL_UNSUPPORTED` (lá quem decide é a carteira).
   */
  async getBindingOptions(agentId: string): Promise<AgentBindingOptions> {
    const { data } = await api.get<AgentBindingOptions>(
      `${BASE}/${encodeURIComponent(agentId)}/binding/options`,
    );
    return data;
  },

  /**
   * Vincular um ENDPOINT à instância — o mesmo `POST /{id}/binding` do
   * número, com o nome genérico (`endpoint_id`) e o canal explícito. As
   * recusas do número continuam valendo, mais as do canal:
   * BINDING_CHANNEL_MISMATCH (endpoint de outro canal) ·
   * BINDING_CHANNEL_UNSUPPORTED (linkedin/facebook) ·
   * BINDING_ENDPOINT_NOT_CONNECTED (conta de Instagram pending/expired) ·
   * BINDING_MAILBOX_NOT_READY (caixa desconectada/em erro/alternativa sem
   * responsável) · BINDING_AGENT_ALREADY_BOUND (troca exige `confirm_swap`,
   * com drenagem — o MESMO fluxo do número).
   *
   * `channel` ausente ⇒ o backend assume o canal da instância. `bindInstanceNumber`
   * continua existindo para os chamadores de WhatsApp — byte a byte.
   */
  async bindInstanceEndpoint(
    agentId: string,
    endpointId: string,
    channel?: string,
    confirmSwap = false,
  ): Promise<AgentInstance> {
    const { data } = await api.post<AgentInstance>(
      `${BASE}/${encodeURIComponent(agentId)}/binding`,
      {
        endpoint_id: endpointId,
        ...(channel ? { channel } : {}),
        confirm_swap: confirmSwap,
      },
    );
    return data;
  },

  /** Desvincular = abrir a DRENAGEM (RN-84) — nunca corta conversa viva. */
  async openDraining(agentId: string): Promise<AgentInstance> {
    const { data } = await api.delete<AgentInstance>(
      `${BASE}/${encodeURIComponent(agentId)}/binding`,
    );
    return data;
  },

  /** 503 nunca "0" — falha de contagem é dita, não zerada. */
  async getDraining(agentId: string): Promise<AgentDrainingStatus> {
    const { data } = await api.get<AgentDrainingStatus>(
      `${BASE}/${encodeURIComponent(agentId)}/binding/draining`,
    );
    return data;
  },

  async closeDraining(
    agentId: string,
  ): Promise<{ closed: boolean; escalated_conversations: number }> {
    const { data } = await api.post<{ closed: boolean; escalated_conversations: number }>(
      `${BASE}/${encodeURIComponent(agentId)}/binding/close-draining`,
      { confirm: true },
    );
    return data;
  },

  /** X-1: união dos dois trilhos pelo carimbo agent_instance_id. */
  async getInstanceMetrics(agentId: string, windowDays = 7): Promise<AgentInstanceMetrics> {
    const { data } = await api.get<AgentInstanceMetrics>(
      `${BASE}/${encodeURIComponent(agentId)}/metrics`,
      { params: { window_days: windowDays } },
    );
    return data;
  },

  /**
   * Drill-down de UM número do Desempenho (decisão do dono, 2026-08-30):
   * cada `metric` reaplica no backend o MESMO filtro que produziu o número
   * do card; `origin`/`origem` abrem as linhas das tabelas de quebra.
   */
  async getInstanceMetricDetail(
    agentId: string,
    params: {
      metric: AgentMetricDetailKind;
      windowDays?: number;
      origin?: string;
      origem?: string;
    },
  ): Promise<AgentMetricDetailResponse> {
    const { data } = await api.get<AgentMetricDetailResponse>(
      `${BASE}/${encodeURIComponent(agentId)}/metrics/detail`,
      {
        params: {
          metric: params.metric,
          window_days: params.windowDays ?? 7,
          ...(params.origin ? { origin: params.origin } : {}),
          ...(params.origem ? { origem: params.origem } : {}),
        },
      },
    );
    return data;
  },

  async getInstanceEvents(agentId: string): Promise<AgentInstanceEventsResponse> {
    const { data } = await api.get<AgentInstanceEventsResponse>(
      `${BASE}/${encodeURIComponent(agentId)}/events`,
    );
    return data;
  },

  /** E-9e — Ensaio da PEÇA do Outbound: dry_run, nada persiste, nada cobra. */
  async rehearsePiece(
    agentId: string,
    dealId?: string | null,
    templateName?: string | null,
    variables?: Record<string, string> | null,
  ): Promise<AgentPieceRehearsal> {
    const { data } = await api.post<AgentPieceRehearsal>(`${BASE}/messaging/rehearse-piece`, {
      agent_id: agentId,
      ...(dealId ? { deal_id: dealId } : {}),
      ...(templateName ? { template_name: templateName } : {}),
      ...(variables ? { variables } : {}),
    });
    return data;
  },
};

// ── Caixas de e-mail do Agente Inbound de E-MAIL (migração 152) ─────────
//
// A caixa do PRÓPRIO cliente, lida e respondida pelo agente — nunca um
// remetente da plataforma (RN-90). O segredo (senha de app) viaja SÓ na
// criação, dentro do corpo, e nunca volta em resposta nenhuma. Literais
// (`/email-mailboxes`) registradas no BFF antes das paramétricas
// `/:agentId/**` — "email-mailboxes" nunca vira um agentId.

const MAILBOXES = `${BASE}/email-mailboxes`;

export const agentesEmailApi = {
  /** As caixas do tenant (gestor+). `vinculavel`/`motivo_bloqueio` já resolvidos. */
  async listMailboxes(): Promise<{ mailboxes: AgentEmailMailbox[] }> {
    const { data } = await api.get<{ mailboxes: AgentEmailMailbox[] }>(MAILBOXES);
    return data;
  },

  /**
   * Conectar por IMAP/SMTP: valida → testa IMAP+SMTP → cifra → grava; com
   * `agent_id`, vincula à instância no MESMO gesto (o assistente não termina
   * com um agente "sem canal"). Recusas COM o código: 422
   * MAILBOX_CONSENT_REQUIRED (LGPD — a caixa carrega conversas de terceiros)
   * · 422 MAILBOX_RESPONSIBLE_REQUIRED (alternativa sem responsável nem
   * tenant_wide) · 422 MAILBOX_CONNECTION_FAILED (a mensagem CONTÉM o erro
   * do servidor — exibir verbatim) · 422 MAILBOX_INVALID · 503
   * MAILBOX_ENCRYPTION_KEY_MISSING (nunca gravação em claro).
   */
  async connectMailbox(
    body: AgentEmailMailboxConnectBody,
  ): Promise<AgentEmailMailboxConnectResponse> {
    const { data } = await api.post<AgentEmailMailboxConnectResponse>(MAILBOXES, body);
    return data;
  },

  /**
   * Google/Microsoft por OAuth — decisão do dono (Q10 c): OAuth onde existe.
   * HOJE responde SEMPRE 409 `EMAIL_OAUTH_NOT_AVAILABLE` com a frase que diz
   * o caminho que funciona (a mesma caixa por IMAP/SMTP com senha de app).
   * O botão existe e mostra essa frase — nunca finge que conectou.
   */
  /**
   * O que o ADMIN do domínio precisa autorizar no Admin console dele —
   * client ID numérico + escopo. Nunca devolve chave (não existe chave: o
   * JWT de delegação é assinado pela identidade de execução do serviço).
   *
   * MEDIDO 2026-09-04: senha de app não existe para conta Google Workspace e
   * não há setting de admin que a libere; desde 2025 o Workspace exige OAuth.
   */
  async googleDelegation(): Promise<AgentEmailDelegationInfo> {
    const { data } = await api.get<AgentEmailDelegationInfo>(
      `${MAILBOXES}/google-delegation`,
      // Um tenant sem a delegação configurada não é um erro a gritar na tela:
      // a seção simplesmente diz que não está pronta.
      { ...({ __skipErrorNotification: true } as object) },
    );
    return data;
  },

  async connectOAuth(provider: 'google' | 'microsoft'): Promise<unknown> {
    const { data } = await api.post<unknown>(
      `${MAILBOXES}/oauth/${encodeURIComponent(provider)}`,
      {},
      // O 409 é a resposta ESPERADA hoje — o toast vermelho global seria
      // ruído por cima da frase que a tela já exibe inline.
      { ...({ __skipErrorNotification: true } as object) },
    );
    return data;
  },

  /**
   * Prova IMAP+SMTP de uma caixa que AINDA NÃO EXISTE — antes de criar o
   * agente. MEDIDO no tenant 4670: contratar o Agente Inbound de e-mail
   * deixou uma instância `paused`, `binding: null` e ZERO caixas, porque a
   * ordem do gesto é `POST /agents` → `POST /email-mailboxes` e toda falha
   * da caixa deixa um órfão.
   *
   * **NÃO grava nada** (nem rascunho) e **não exige consentimento** (provar
   * credencial não lê mensagem nenhuma). `ok: false` volta em **200** — o
   * desfecho da prova está no CORPO, e por isso a chamada pula o toast
   * global: a tela mostra `imap_ok`/`smtp_ok`/`error` inline, campo a campo.
   */
  async probeMailbox(body: AgentEmailMailboxProbeBody): Promise<AgentEmailMailboxProbeResponse> {
    const { data } = await api.post<AgentEmailMailboxProbeResponse>(
      `${MAILBOXES}/test-connection`,
      body,
      { ...({ __skipErrorNotification: true } as object) },
    );
    return data;
  },

  /**
   * Reescreve o ESCOPO de uma caixa já conectada (migração 154) — QUAIS
   * mensagens o agente responde. O mapeamento muda com o negócio (um assunto
   * novo de campanha, o domínio de um cliente novo), e sem esta rota trocar a
   * lista obrigaria a desconectar e redigitar a senha de app.
   *
   * Declaração vazia é recusada com 422 `MAILBOX_SCOPE_REQUIRED` — ausência
   * de declaração nunca vira permissão, e "responde tudo" (`{modo:'todas'}`)
   * continua sendo um ato consciente.
   */
  async setMailboxScope(
    mailboxId: string,
    escopo: { modo: 'todas' | 'listados'; remetentes?: string[]; assuntos?: string[] },
  ): Promise<AgentEmailMailbox> {
    const { data } = await api.patch<AgentEmailMailbox>(
      `${MAILBOXES}/${encodeURIComponent(mailboxId)}/scope`,
      { escopo },
    );
    return data;
  },

  /**
   * Reescreve DE QUEM é a caixa — e, sendo compartilhada, quem recebe o lead.
   *
   * Decisão do dono (2026-09-05). Existe pelo MESMO motivo do
   * `setMailboxScope`: quem sai da empresa, quem troca de time e quem assume a
   * `vendas@` mudam com o negócio, e sem esta rota trocar o responsável
   * obrigaria a desconectar e redigitar a senha de app.
   *
   * Os três campos viajam JUNTOS porque são UMA decisão — mandá-los em
   * separado deixaria passar o estado que a RN-95 recusa (compartilhada, sem
   * rodízio e sem responsável = caixa que não recebe nada, em silêncio).
   */
  async setMailboxResponsible(
    mailboxId: string,
    corpo: {
      tipo: AgentEmailMailboxTipo;
      tenant_wide?: boolean;
      responsavel_user_id?: number | null;
    },
  ): Promise<AgentEmailMailbox> {
    const { data } = await api.patch<AgentEmailMailbox>(
      `${MAILBOXES}/${encodeURIComponent(mailboxId)}/responsible`,
      corpo,
    );
    return data;
  },

  /** Prova IMAP+SMTP ao vivo; devolve a caixa + `prova {imap_ok, smtp_ok, error}`. */
  async testMailbox(mailboxId: string): Promise<AgentEmailMailboxTestResponse> {
    const { data } = await api.post<AgentEmailMailboxTestResponse>(
      `${MAILBOXES}/${encodeURIComponent(mailboxId)}/test`,
      {},
    );
    return data;
  },

  /**
   * Desconectar: desliga a leitura, DESCARTA o segredo e FECHA o vínculo —
   * sem drenagem (uma caixa desconectada não termina conversa nenhuma).
   */
  async disconnectMailbox(mailboxId: string): Promise<AgentEmailMailboxDisconnectResponse> {
    const { data } = await api.delete<AgentEmailMailboxDisconnectResponse>(
      `${MAILBOXES}/${encodeURIComponent(mailboxId)}`,
    );
    return data;
  },
};

// ── Nível de autonomia (Marketing Hub v3 · Onda 1) ─────────────────────
//
// Contrato pinado: `GET /api/v1/agentes/autonomy-level` (via BFF — o path
// do BFF é o path do backend byte a byte). A resposta crua é devolvida como
// veio; a normalização (snake/camel, `null`s) vive em `utils/autonomia.ts`,
// que é onde a regra "o rótulo sai de `effective`" é testada.

export const agentesAutonomyApi = {
  async getAutonomyLevel(): Promise<unknown> {
    const { data } = await api.get<unknown>('/agentes/autonomy-level', {
      ...({ __skipErrorNotification: true } as object),
    });
    return data;
  },
};

// ── Ensaio em WhatsApp Real & Templates (2026-09-12) ───────────────────

export const agentesRehearsalApi = {
  /** Lista templates aprovados da conta com texto e variáveis. */
  async listTemplates(channel = 'whatsapp'): Promise<RehearsalTemplatesResponse> {
    const { data } = await api.get<RehearsalTemplatesResponse>(`${MESSAGING}/templates`, {
      params: { channel },
    });
    return data;
  },

  /** Lista números autorizados para teste em WhatsApp real pelo tenant. */
  async listTestNumbers(): Promise<{ test_numbers: RehearsalAuthorizedNumber[]; numbers: RehearsalAuthorizedNumber[] }> {
    const { data } = await api.get<{ test_numbers?: RehearsalAuthorizedNumber[]; numbers?: RehearsalAuthorizedNumber[] }>(
      `${MESSAGING}/rehearsal/test-numbers`,
    );
    const nums = data.test_numbers ?? data.numbers ?? [];
    return { test_numbers: nums, numbers: nums };
  },

  /** Cadastra um novo número autorizado na whitelist de ensaio do tenant. */
  async createTestNumber(phone: string, label: string): Promise<RehearsalAuthorizedNumber> {
    const { data } = await api.post<RehearsalAuthorizedNumber>(
      `${MESSAGING}/rehearsal/test-numbers`,
      { phone, label },
    );
    return data;
  },

  /** Remove um número autorizado da whitelist. */
  async deleteTestNumber(numberId: string): Promise<{ ok: boolean }> {
    const { data } = await api.delete<{ ok: boolean }>(
      `${MESSAGING}/rehearsal/test-numbers/${encodeURIComponent(numberId)}`,
    );
    return data;
  },

  /** Dispara template/mensagem inicial e abre sessão ao vivo no WhatsApp real. */
  async startLiveSession(body: {
    agent_id: string;
    phone: string;
    template_name?: string;
    variables?: Record<string, string>;
  }): Promise<RehearsalLiveSession> {
    const { data } = await api.post<RehearsalLiveSession>(
      `${MESSAGING}/rehearsal/live-session`,
      body,
    );
    return data;
  },

  /** Consulta estado e eventos da sessão ao vivo (usado em polling de espelhamento). */
  async getLiveSession(sessionId: string): Promise<RehearsalLiveSession> {
    const { data } = await api.get<RehearsalLiveSession>(
      `${MESSAGING}/rehearsal/live-session/${encodeURIComponent(sessionId)}`,
    );
    return data;
  },

  /** Encerra a sessão ativa de ensaio no WhatsApp real. */
  async endLiveSession(sessionId: string): Promise<RehearsalLiveSession> {
    const { data } = await api.post<RehearsalLiveSession>(
      `${MESSAGING}/rehearsal/live-session/${encodeURIComponent(sessionId)}/end`,
      {},
    );
    return data;
  },
};

/**
 * API para consulta de conversas e atividades agrupadas por Empresas e Contatos.
 */
export const agentesConversationsApi = {
  async getConversationsAndActivities(params?: {
    agentId?: string;
    search?: string;
    skip?: number;
    limit?: number;
  }): Promise<AgentConversationsResponse> {
    const queryParams: Record<string, string | number> = {};
    if (params?.agentId) queryParams.agent_id = params.agentId;
    if (params?.search) queryParams.search = params.search;
    if (params?.skip !== undefined) queryParams.skip = params.skip;
    if (params?.limit !== undefined) queryParams.limit = params.limit;

    const { data } = await api.get<AgentConversationsResponse>(
      `${MESSAGING}/conversations-activities`,
      { params: queryParams },
    );
    return data;
  },
};

/**
  * API do Especialista em Evolução de Conversas & Auto-Otimização (incremental + pacing + sugestões auditáveis).
  */
export const agentesEvolutionApi = {
  /** Obtém status do watermark/checkpoint, pacing atual e métricas de mensagens. */
  async getStatus(agentId: string): Promise<AgentEvolutionStatusResponse> {
    const { data } = await api.get<AgentEvolutionStatusResponse>(
      `${BASE}/${encodeURIComponent(agentId)}/evolution/status`,
    );
    return data;
  },

  /** Atualiza o ritmo conversacional (ansiedade, min_turnos, auto-aplicação diária) gerando perfil N+1. */
  async updatePacing(
    agentId: string,
    pacingData: UpdatePacingRequest,
  ): Promise<{
    agent_instance_id: string;
    new_profile_version: number;
    pacing_config: AgentEvolutionPacingConfig;
  }> {
    const { data } = await api.put(
      `${BASE}/${encodeURIComponent(agentId)}/evolution/pacing`,
      pacingData,
    );
    return data;
  },

  /** Dispara compilação e análise incremental de novas conversas. */
  async runAnalysis(
    agentId: string,
    batchLimit = 500,
  ): Promise<RunAnalysisResponse> {
    const { data } = await api.post<RunAnalysisResponse>(
      `${BASE}/${encodeURIComponent(agentId)}/evolution/analyze`,
      { batch_limit: batchLimit },
    );
    return data;
  },

  /** Lista sugestões geradas pela IA especialista com histórico e filtros. */
  async listSuggestions(
    agentId: string,
    params?: { status?: string; limit?: number; skip?: number },
  ): Promise<{ suggestions: AgentEvolutionSuggestion[] }> {
    const { data } = await api.get<{ suggestions: AgentEvolutionSuggestion[] }>(
      `${BASE}/${encodeURIComponent(agentId)}/evolution/suggestions`,
      { params },
    );
    return data;
  },

  /** Aplica a melhoria no agente em 1 clique gerando versão N+1 no AgentProfile. */
  async applySuggestion(
    agentId: string,
    suggestionId: string,
  ): Promise<{
    suggestion_id: string;
    status: string;
    applied_at: string;
    applied_profile_version: number;
    target_block: string;
  }> {
    const { data } = await api.post(
      `${BASE}/${encodeURIComponent(agentId)}/evolution/suggestions/${encodeURIComponent(suggestionId)}/apply`,
    );
    return data;
  },

  /** Descarta a sugestão com motivo registrado para auditoria. */
  async dismissSuggestion(
    agentId: string,
    suggestionId: string,
    reason?: string,
  ): Promise<{
    suggestion_id: string;
    status: string;
    dismissed_at: string;
    dismiss_reason: string;
  }> {
    const { data } = await api.post(
      `${BASE}/${encodeURIComponent(agentId)}/evolution/suggestions/${encodeURIComponent(suggestionId)}/dismiss`,
      { reason },
    );
    return data;
  },

  /** Conversa com o Co-piloto de evolução e diálogo da IA. */
  async copilotChat(
    agentId: string,
    messages: CopilotChatMessage[],
  ): Promise<CopilotChatResponse> {
    const { data } = await api.post<CopilotChatResponse>(
      `${BASE}/${encodeURIComponent(agentId)}/evolution/chat`,
      { messages },
    );
    return data;
  },

  /** Obtém histórico persistido de mensagens com o co-piloto. */
  async getChatHistory(
    agentId: string,
    limit = 50,
  ): Promise<CopilotChatHistoryResponse> {
    const { data } = await api.get<CopilotChatHistoryResponse>(
      `${BASE}/${encodeURIComponent(agentId)}/evolution/chat`,
      { params: { limit } },
    );
    return data;
  },

  /** Limpa histórico de mensagens com o co-piloto. */
  async clearChatHistory(
    agentId: string,
  ): Promise<CopilotChatClearResponse> {
    const { data } = await api.delete<CopilotChatClearResponse>(
      `${BASE}/${encodeURIComponent(agentId)}/evolution/chat`,
    );
    return data;
  },

  /** Aplica proposta gerada pelo Co-piloto gerando versão N+1 no perfil do agente. */
  async applyProposal(
    agentId: string,
    proposal: CopilotProposal,
  ): Promise<ApplyProposalResponse> {
    const { data } = await api.post<ApplyProposalResponse>(
      `${BASE}/${encodeURIComponent(agentId)}/evolution/proposals/apply`,
      proposal,
    );
    return data;
  },
};



